import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { ArrowSquareOut, PencilSimple, SuitcaseSimple } from "@phosphor-icons/react"
import { ListItem, ListText } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { DateOnly } from "@/lib/date-only"
import { HYPHEN_CHARACTER } from "@/lib/format-date"
import { cn } from "@/lib/utils"
import { DocumentProps } from "@/modules/patients/detail/progress-note/types/document"
import { ChSickLeaveCertificateDrawer } from "@/modules/patients/detail/sick-leave-certificate/ch/ch-sick-leave-certificate-drawer"
import { chSickLeaveFragment$key } from "@/modules/patients/detail/sick-leave-certificate/ch/services/__generated__/chSickLeaveFragment.graphql"
import { chSickLeaveFragment } from "@/modules/patients/detail/sick-leave-certificate/ch/services/chSickLeaveFragment"
import { DeSickLeaveCertificateDrawer } from "@/modules/patients/detail/sick-leave-certificate/de/de-sick-leave-certificate-drawer"
import { deSickLeaveFragment$key } from "@/modules/patients/detail/sick-leave-certificate/de/services/__generated__/deSickLeaveFragment.graphql"
import { deSickLeaveFragment } from "@/modules/patients/detail/sick-leave-certificate/de/services/deSickLeaveFragment"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface SickLeaveCertificateDocumentProps extends DocumentProps {
  sickLeaveRef: deSickLeaveFragment$key | chSickLeaveFragment$key
  progressNoteId: string
}

const SickLeaveCertificateDocument = ({
  sickLeaveRef,
  progressNoteId,
  isEditable = true,
  isConclusion,
  isSigned,
  isSent,
  onSelect,
}: SickLeaveCertificateDocumentProps) => {
  const { t } = useTranslation()
  const countryCode = useCountryParam()
  const data = useFragment(countryCode === "CH" ? chSickLeaveFragment : deSickLeaveFragment, sickLeaveRef) ?? {}
  const selectedDocumentId = useConcludeDialogStoreContext((state) => state.selectedDocument?.id)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)
  const isSelected = isConclusion && selectedDocumentId === data.id

  if (!data.id) return null

  return (
    <ListItem
      className={cn(
        "group hover:bg-surface-hovered relative flex items-center hover:cursor-pointer",
        isSelected && "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed",
        !isEditable && !isConclusion && !isSent && "cursor-auto!",
      )}
      data-testid="sick-leave-document-item"
      onClick={() => {
        if (!isEditable) {
          onSelect()
          return
        }

        const drawerContent = () =>
          countryCode === "CH" ? (
            <ChSickLeaveCertificateDrawer progressNoteId={progressNoteId} sickLeaveRef={sickLeaveRef} />
          ) : (
            <DeSickLeaveCertificateDrawer progressNoteId={progressNoteId} sickLeaveRef={sickLeaveRef} />
          )
        openDrawer(drawerContent, "SickLeaveCertificateDrawer")
      }}>
      <SuitcaseSimple className={isSelected ? "text-on-primary-surface" : "text-on-surface-subdued"} />
      <div className="ml-2 min-w-0">
        <ListText className={isSelected ? "text-on-primary-surface" : ""}>
          {countryCode === "CH"
            ? t("episodeOfCare.progressNote.sickLeaveCertificate.ch")
            : t("episodeOfCare.progressNote.sickLeaveCertificate.de")}
        </ListText>
        <ListText
          data-testid="sick-leave-document-time-frame"
          variant="secondary"
          className={isSelected ? "text-on-primary-surface" : ""}>{`${new DateOnly(
          data.from,
        ).toDisplayableString()} ${HYPHEN_CHARACTER} ${new DateOnly(data.to).toDisplayableString()}`}</ListText>
      </div>
      {isEditable && (
        <div className="absolute right-2 ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <PencilSimple className="text-on-surface-subdued" data-testid="sick-leave-edit-button" />
          </div>
        </div>
      )}
      {!isConclusion && isSent && (
        <div className="absolute right-2 ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <ArrowSquareOut className="text-on-surface-subdued" data-testid="sick-leave-view-document-button" />
          </div>
        </div>
      )}
      {isConclusion && (triggeredValidation || isSigned) && (
        <Tag
          className="ml-auto group-hover:hidden"
          type={isSigned ? "success" : "critical"}
          label={isSigned ? t("patient.conclude.signed") : t("patient.conclude.signThis")}
        />
      )}
    </ListItem>
  )
}

export { SickLeaveCertificateDocument }
